import type { Match, Player } from '../types';
import { preferMatchPositionOverride } from './player';

/** Preserve match settings even when their owner is absent or the lineup is cleared. */
export const retainMatchPositionOverrides = (
  reserves: Player[],
  snapshots: Array<Player | null>,
  lineup: Record<string, Player | null>,
  excludedIds: string[] = [],
): Player[] => {
  const excluded = new Set(excludedIds);
  const positioned = new Set(Object.values(lineup).filter(Boolean).map(player => player!.id));
  const result = new Map(reserves.filter(player => !positioned.has(player.id) && !excluded.has(player.id)).map(player => [player.id, player]));
  snapshots.forEach(player => {
    if (player?.matchPositionOverride && !positioned.has(player.id) && !excluded.has(player.id)) {
      const existing = result.get(player.id);
      result.set(player.id, existing ? preferMatchPositionOverride(player, existing) : player);
    }
  });
  return [...result.values()];
};

export const buildMatchPositionUpdate = (
  match: Pick<Match, 'lineup' | 'reserve_players'>,
  draftLineup: Record<string, Player | null>,
  player: Player,
  isEligible: (position: string) => boolean,
): { lineup: Record<string, Player | null>; reserve_players: Player[]; substitution_schedule: null } => {
  const lineup = Object.fromEntries(Object.entries(draftLineup).map(([position, assigned]) => [
    position,
    assigned?.id === player.id ? (isEligible(position) ? player : null) : assigned,
  ]));
  const reserves = (match.reserve_players || []).filter(candidate => candidate.id !== player.id);
  return {
    lineup,
    reserve_players: retainMatchPositionOverrides(reserves, [
      ...Object.values(match.lineup || {}).filter(candidate => candidate?.id !== player.id),
      player,
    ], lineup),
    substitution_schedule: null,
  };
};

/** Durable position settings outlive field occupation and squad membership. */
export function matchPositionSettings(match: Pick<Match, 'preparation_settings' | 'lineup' | 'reserve_players'>) {
  const overrides = { ...match.preparation_settings?.match_position_overrides };
  [...Object.values(match.lineup || {}), ...(match.reserve_players || [])].forEach(player => {
    if (player?.matchPositionOverride && !overrides[player.id]) {
      const { positionPreferences, positionPreferenceVariant, positionPreferencesByVariant, hasPositionPriority } = player;
      overrides[player.id] = { positionPreferences, positionPreferenceVariant, positionPreferencesByVariant, hasPositionPriority };
    }
  });
  return overrides;
}

export function applyMatchPositionSettings(players: Player[], overrides: Match['preparation_settings']) {
  return players.map(player => {
    const positions = overrides?.match_position_overrides?.[player.id];
    return positions ? { ...player, ...positions, matchPositionOverride: true } : player;
  });
}
